/// <amd-module name="TFS/Admin/Groups/Pivots/Members/Components/MembersPivotInitializer" />
import * as React from "react";
import { connect } from "react-redux";
import { PersonaDetailsProvider } from "TFS/Admin/Common/Providers/PersonaDetailsProvider";
import { FilterablePivot, IFilterablePivotOwnProps, IFilterablePivotProps } from "TFS/Admin/Groups/Common/Components/FilterablePivot";
import * as Constants from "TFS/Admin/Groups/Common/Constants";
import { CustomGraphMember, IOrganizationGroupsAwareState } from "TFS/Admin/Groups/Common/Contracts";
import { GroupsViewActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions";
import * as PageSelectors from "TFS/Admin/Groups/Common/Redux/GroupsViewSelectors";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { convertGraphMemberToIIdentity } from "TFS/Admin/Groups/Common/Util/CommonTypeConverters";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { MembersPivotActions } from "TFS/Admin/Groups/Pivots/Members/Redux/MembersPivotActions";
import * as MemberSelectors from "TFS/Admin/Groups/Pivots/Members/Redux/MembersPivotSelectors";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { Card } from "VSSUI/Card";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { IIdentity } from "VSSUI/IdentityPicker";
import { ListSelection } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Pill, PillSize } from "VSSUI/Pill";
import { PillGroup } from "VSSUI/PillGroup";
import {
    ITableColumn,
    ITableRow,
    ITableRowDetails,
    SimpleTableCell,
    Table,
    TableColumnStyle,
    TableLoadingRow,
    TableRow,
    TwoLineTableCell
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";

import { aadPrefix } from "TFS/Admin/Common/Constants/Constants";
import { IEntityType } from "TFS/Admin/Groups/Common/Constants";
import { announce } from "VSS/Core/Util/Accessibility";
import { format, localeFormat, startsWith } from "VSS/Core/Util/String";

export interface IMembersPivotContainerProps extends IFilterablePivotProps {}

export interface IMembersPivotInitializerOwnProps extends IMembersPivotContainerProps, IFilterablePivotOwnProps {
    loading: boolean;
    identities: CustomGraphMember[];
    moreData: boolean;
    totalIdentities: number;
    continutationToken?: string[];
    initComplete: boolean;
    selectedMember: CustomGraphMember;
    selectedIdentity: IIdentity;
    messageCardText?: string;
    messageCardSeverity?: MessageCardSeverity;
    scopeDescriptor: string | undefined;
    permissionsPage: Constants.IPermissionPage;
    isCollectionNavigationAllowed: boolean;
}

export type IMembersPivotInitializerProps = IMembersPivotInitializerOwnProps & typeof ActionsToDispatch;

export class MembersPivotInitializer extends FilterablePivot<IMembersPivotInitializerProps> {
    private _itemProvider: IObservableArray<CustomGraphMember>;
    private _selection = new ListSelection(false);
    private _personaDetailsProvider: PersonaDetailsProvider;

    constructor(props: IMembersPivotInitializerProps) {
        super(props);

        this.props.setFilterPlaceholder(Resources.SearchUsersByPrefix);
        this.props.initView(this.props.scopeDescriptor, this.props.permissionsPage);

        const userRows: any[] = this.props.identities.slice();
        if (this.props.moreData && this.props.identities.length) {
            userRows.push(new ObservableValue<any | undefined>(undefined));
        }
        this._itemProvider = new ObservableArray<any>(userRows);
        this._personaDetailsProvider = new PersonaDetailsProvider(this.props.pageContext);
    }

    public componentDidUpdate(prevProps: IMembersPivotInitializerProps) {
        const userRows: any[] = this.props.identities.slice();

        if (this.props.moreData && this.props.identities.length) {
            userRows.push(new ObservableValue<any | undefined>(undefined));
        }

        this._itemProvider.splice(0, this._itemProvider.length, ...userRows);

        // Narrator not built in FilterablePivot because of the multi-render issue
        if (prevProps.searchResult && prevProps.searchResult.status != Constants.SearchStatus.Completed && this._isSearchCompleted()) {
            announce(format(Resources.SearchResultsCount, this.props.searchResult.resultItems.length));
        }
    }

    private _isSearchCompleted() {
        return this.props.searchResult.status == Constants.SearchStatus.Completed;
    }

    /***Filterable Pivot Methods */
    protected _getItemsName(): string {
        return Resources.Users;
    }

    protected _isProjectScope(): boolean {
        return this.props.scopeDescriptor ? true : false;
    }

    protected _isCollectionNavigationAllowed(): boolean {
        return this.props.isCollectionNavigationAllowed;
    }

    protected _onExpandSearch(): void {
        this.props.logTelemetry(GroupsComponent.UsersPivot, "Expanded search", {});
        this.props.searchMembers(this.props.searchResult.keyword, "", "", true, Constants.IPermissionPage.Project);
    }

    protected _isContentReady(): boolean {
        return !this.props.loading && !this._isFiltering();
    }

    protected _getFilteredContentColumns(): ITableColumn<CustomGraphMember>[] {
        return this._getColumns();
    }

    protected _onRenderRow(index: number, item: CustomGraphMember, details: ITableRowDetails<CustomGraphMember>): JSX.Element {
        return <TableRow key={index} index={index} details={details} />;
    }

    protected _renderContent(): JSX.Element {
        return this._getRenderElements(this._itemProvider, this._getColumns(), false);
    }

    protected _renderFilteredContent(): JSX.Element {
        const { resultItems } = this.props.searchResult;
        return resultItems.length > 0
            ? this._getRenderElements(new ObservableArray<any>(resultItems), this._getFilteredContentColumns(), true)
            : this._getZeroData();
    }

    protected _onFilterChanged(keyword: string): void {
        this.props.logTelemetry(GroupsComponent.UsersPivot, "Filter changed", {});
        this.props.searchMembers(
            keyword,
            this.props.searchResult ? this.props.searchResult.keyword : "",
            this.props.scopeDescriptor,
            false,
            this.props.permissionsPage
        );
    }

    protected _componentDidMount() {
        this.props.logTelemetry(GroupsComponent.UsersPivot, "Pivot loaded", {});
    }
    /***End Filterable Pivot methods */

    private _getRenderElements(
        itemProvider: IObservableArray<CustomGraphMember>,
        columns: ITableColumn<CustomGraphMember>[],
        filtered: boolean
    ): JSX.Element {
        return (
            <div>
                {this.props.messageCardText ? (
                    <MessageCard
                        className="permissions-card"
                        severity={this.props.messageCardSeverity}
                        onDismiss={() => this.props.onDismissMessageCard()}
                    >
                        {this.props.messageCardText}
                    </MessageCard>
                ) : null}
                <Card className="flex-grow bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }}>
                    <div className="flex-column">
                        <CustomHeader className="bolt-header-with-commandbar no-bottom-padding">
                            <HeaderTitleArea className="flex-row table-title-text">
                                <PillGroup>
                                    <span className="text-ellipsis">{Resources.Total}</span>
                                    <Pill containsCount={true} excludeTabStop={true} size={PillSize.compact}>
                                        {this.props.identities.length + (this.props.moreData ? "+" : "")}
                                    </Pill>
                                    {this.props.searchResult && this._isSearchCompleted() && (
                                        <>
                                            <span className="text-ellipsis">{Resources.Filtered}</span>
                                            <Pill containsCount={true} excludeTabStop={true} size={PillSize.compact}>
                                                {this.props.searchResult.resultItems.length}
                                            </Pill>
                                        </>
                                    )}
                                </PillGroup>
                            </HeaderTitleArea>
                        </CustomHeader>
                        <Table
                            itemProvider={itemProvider}
                            columns={columns}
                            onActivate={this._onRowClicked}
                            selection={this._selection}
                            singleClickActivation={true}
                            renderLoadingRow={this._renderLoadRow}
                        />
                    </div>
                </Card>
            </div>
        );
    }

    private _onRowClicked = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<CustomGraphMember>) => {
        this.props.logTelemetry(GroupsComponent.UsersPivot, "Open users detail view", {});
        const { ctrlKey, metaKey, shiftKey, button } = (event || {}) as React.MouseEvent<HTMLElement>;

        if (!ctrlKey && !metaKey && !shiftKey && button !== 1) {
            this.props.selectedMemberChanged(tableRow.data);

            // Prevent the default navigation from taking place.
            if (event) {
                event.preventDefault();
            }
        }
    };

    private _getColumns(): ITableColumn<CustomGraphMember>[] {
        return [
            {
                id: "Name",
                name: Resources.Name,
                renderCell: this._renderNameColumn,
                width: new ObservableValue(-66),
                columnStyle: TableColumnStyle.Primary,
                onSize: this._onSizeColumn
            },
            {
                id: "type",
                name: Resources.TypeTableTitle,
                renderCell: this._renderTypeColumn.bind(this),
                width: new ObservableValue(-33),
                columnStyle: TableColumnStyle.Secondary,
                onSize: this._onSizeColumn
            }
        ];
    }

    private _renderTypeColumn(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<CustomGraphMember>,
        tableItem: CustomGraphMember
    ): JSX.Element {
        const isAadMember = startsWith(tableItem.descriptor, aadPrefix, true);
        const isServicePrincipal = tableItem.subjectKind === IEntityType.ServicePrincipal;
        const typeString = localeFormat(isAadMember ? "{0} {1}" : "{1}", aadPrefix, isServicePrincipal ? tableItem.metaType : tableItem.subjectKind);
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName="display-users-details">
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis">{typeString}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderNameColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<CustomGraphMember>,
        tableItem: CustomGraphMember
    ): JSX.Element => {
        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={columnIndex}
                line1={
                    <Tooltip overflowOnly={true}>
                        <span className="fontWeightSemiBold bolt-table-two-line-cell-item text-ellipsis">{tableItem.displayName}</span>
                    </Tooltip>
                }
                line2={
                    <Tooltip overflowOnly={true}>
                        <span className="fontSizeMS bolt-table-two-line-cell-item secondary-text flex-row flex-baseline text-ellipsis">
                            {tableItem.subjectKind == IEntityType.ServicePrincipal
                                ? (tableItem as any).applicationId
                                : tableItem.mailAddress?.length > 0
                                ? tableItem.mailAddress
                                : ""}
                        </span>
                    </Tooltip>
                }
                iconProps={{
                    render: (className?: string | undefined): JSX.Element => this._renderImageIcon(tableItem, className)
                }}
            />
        );
    };

    private _renderImageIcon(tableItem: CustomGraphMember, className?: string | undefined): JSX.Element {
        return (
            <Persona
                className="display-members-persona"
                size={PersonaSize.size40}
                identity={convertGraphMemberToIIdentity(this.props.pageContext, tableItem)}
                personaProvider={this._personaDetailsProvider}
            />
        );
    }

    private _onRenderMissingItem = (): null => {
        this.props.logTelemetry(GroupsComponent.UsersPivot, "Load more users on scroll", {});
        if (this.props.continutationToken && this.props.continutationToken.length > 0) {
            this.props.onRenderMissingItem(this.props.continutationToken, this.props.scopeDescriptor, this.props.permissionsPage);
        }
        return null;
    };

    private _onSizeColumn(event: MouseEvent, index: number, width: number, column: ITableColumn<CustomGraphMember>) {
        (column.width as IObservableValue<number>).value = width;
    }

    private _renderLoadRow = (index: number, details: ITableRowDetails<CustomGraphMember>): JSX.Element => {
        this._onRenderMissingItem();

        return <TableLoadingRow columns={this._getColumns()} details={details} key={index} rowIndex={index} />;
    };
}

// Update component props whenever the store's state changes
function mapStateToProps(
    state: IOrganizationGroupsAwareState,
    providedProps: IMembersPivotContainerProps
): Partial<IMembersPivotInitializerOwnProps> {
    return {
        ...providedProps,
        identities: MemberSelectors.getMembers(state),
        moreData: MemberSelectors.fetchMoreDataOnLastRow(state),
        totalIdentities: MemberSelectors.getTotalIdentityCount(state),
        continutationToken: MemberSelectors.getContinuationToken(state),
        initComplete: MemberSelectors.getInitComplete(state),
        selectedMember: PageSelectors.getSelectedMember(state),
        messageCardText: MemberSelectors.getMessageCardText(state),
        messageCardSeverity: MemberSelectors.getMessageCardSeverity(state),
        loading: MemberSelectors.showLoadingPanel(state),
        searchResult: MemberSelectors.getSearchResult(state),

        // Page Selectors
        scopeDescriptor: PageSelectors.getScopeDescriptor(state),
        permissionsPage: PageSelectors.getPermissionPage(state),
        isCollectionNavigationAllowed: PageSelectors.isCollectionNavigationAllowed(state)
    };
}

// Hook up action creators to reducer
const ActionsToDispatch = {
    initView: MembersPivotActions.initView,
    disposeView: MembersPivotActions.disposeView,
    onRenderMissingItem: MembersPivotActions.onRenderMissingItem,
    onDismissMessageCard: MembersPivotActions.onDismissMessageCard,
    searchMembers: MembersPivotActions.searchMembers,

    // Group view actions
    selectedMemberChanged: GroupsViewActions.selectedMemberChanged,
    logTelemetry: GroupsViewActions.logTelemetry,
    setFilterPlaceholder: GroupsViewActions.setFilterPlaceholder
};

export const ConnectedMembersPivotInitializer = connect(mapStateToProps, ActionsToDispatch)(MembersPivotInitializer);
